// ------------- Labels & Aliases -------------
export const DIM_LABELS = {
  geo: "Geography",
  business: "Business/Customer Segment",
  product: "Product Category",
  customer: "Customer Type",
  channel: "Channel Type",
  application: "Application Market",
};

// map our canonical keys -> possible column names in rows
const ALIASES = {
  geo: ["geo", "geography", "country", "region", "market"],
  business: ["business", "business_segment", "segment", "business_unit", "end_market"],
  product: ["product", "product_line", "product_category"],
  customer: ["customer", "customer_type", "account", "customer_group"],
  channel: ["channel", "channel_type", "sales_channel"],
  application: ["application", "application_market"],
};

// ------------- Helpers -------------
function toNum(v) {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const s = String(v).replace(/[$,\s]/g, "").replace(/[MB]$/i, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function fmtMoneyAuto(millions) {
  const n = toNum(millions);
  return n >= 1000 ? `$${(n / 1000).toFixed(1)}B` : `$${Math.round(n).toLocaleString()}M`;
}

function groupBy(arr, keyFn) {
  const m = new Map();
  for (const r of arr) {
    const k = keyFn(r);
    m.set(k, (m.get(k) || []).concat(r));
  }
  return m;
}

function resolveColumns(rows) {
  const sample = rows[0] || {};
  const present = (n) => n && Object.prototype.hasOwnProperty.call(sample, n);
  const first = (names) => names.find(present) || null;

  const detected = {};
  for (const [k, names] of Object.entries(ALIASES)) detected[k] = first(names);

  const used = new Set();
  const colMap = {};
  for (const [k, col] of Object.entries(detected)) {
    if (col && !used.has(col)) { colMap[k] = col; used.add(col); }
    else colMap[k] = null;
  }
  return colMap;
}

function hasAnyNonBlank(rows, col) {
  if (!col) return false;
  for (const r of rows) {
    const v = r?.[col];
    if (v !== null && v !== undefined && String(v).trim() !== "") return true;
  }
  return false;
}

// YoY mean from [ [year,total], ... ]
function meanYoYFromPairs(pairs) {
  if (!pairs || pairs.length < 2) return undefined;
  const sorted = [...pairs].sort((a,b)=> String(a[0]).localeCompare(String(b[0])));
  let sum = 0, cnt = 0;
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i-1][1], cur = sorted[i][1];
    if (prev > 0) { sum += ((cur - prev) / prev) * 100; cnt++; }
  }
  return cnt ? +(sum / cnt).toFixed(1) : undefined;
}

// Try several field names for growth on a row (more robust)
const DEFAULT_ROW_GROWTH_FIELDS = [
  "growthPct", "growth_percent", "yoyGrowth", "yoy_pct", "YoY", "growth"
];

// Revenue-weighted average of growth across rows for any of the given fields
function weightedAvgGrowthFlexible(rows, valueField, growthFields) {
  let num = 0, den = 0;
  for (const r of rows) {
    let g;
    for (const f of growthFields) {
      const val = r[f];
      const p = (typeof val === "string") ? parseFloat(val) : val;
      if (Number.isFinite(p)) { g = p; break; }
    }
    const v = toNum(r[valueField]);
    if (Number.isFinite(g) && v > 0) { num += g * v; den += v; }
  }
  return den > 0 ? +(num / den).toFixed(1) : undefined;
}

// ------------- NEW: normalize new JSON -> flat rows + meta -------------
/**
 * Accepts your new JSON document that looks like:
 * {
 *   metadata: { company_name, fiscal_year, total_revenue_millions, ... },
 *   combined_analysis: {
 *     all_segments: [...],
 *     all_sources: [...],
 *     all_assumptions: [...]
 *     // (optionally) all_base_facts: [...]
 *   }
 * }
 *
 * Returns: { rows, meta, yearTotalsOverride, sources, baseFacts, assumptions }
 */
export function normalizeRevenuePayload(doc) {
  if (!doc) return { rows: [], meta: null, yearTotalsOverride: [], sources: [], baseFacts: [], assumptions: [] };

  const meta = doc.metadata || {};
  const ca = doc.combined_analysis || {};

  const rows = Array.isArray(ca.all_segments) ? ca.all_segments : [];

  const sources = Array.isArray(ca.all_sources) ? ca.all_sources : [];

  // Keep base facts SEPARATE from assumptions; if not provided, synthesize simple facts
  let baseFacts = Array.isArray(ca.all_base_facts) ? ca.all_base_facts : [];
  if (!baseFacts.length) {
    // synthesize from each source.key_data_extracted (still separate from assumptions)
    baseFacts = sources.flatMap((s) =>
      (s.key_data_extracted || []).map((text, i) => ({
        source_id: s.source_id,
        fact_id: `${s.source_id}#${i + 1}`,
        text,
      }))
    );
  }

  const assumptions = Array.isArray(ca.all_assumptions) ? ca.all_assumptions : [];

  // If you ever pass explicit year totals, supply here; otherwise empty
  const yearTotalsOverride = [];

  return { rows, meta, yearTotalsOverride, sources, baseFacts, assumptions };
}

// ------------- Core builder (same logic as old, with small tweaks) -------------
/**
 * opts:
 *  - company?: { name: string, annualRevenueOverride?: number }
 *  - context?: { fiscalYear?: string|number, avgGrowthPct?: number }
 *  - yearTotalsOverride?: Array<[year, totalMillions]>
 *  - rowGrowthFields?: string[]
 *  - defaultAvgGrowthPct?: number
 *  - sources?: Source[]
 *  - baseFacts?: BaseFact[]
 *  - assumptions?: any[]
 */
export function buildRevenueTeardownData(
  rows,
  preferredOrder = ["geo","business","product","application","customer","channel"],
  valueField = "revenue_millions",
  _pctField,
  opts = {}
) {
  if (!rows?.length) {
    return {
      company: opts.company || { name: undefined },
      context: { fiscalYear: opts.context?.fiscalYear, totalRevenue: "$0M", avgGrowthPct: 0 },
      dimensions: [],
      root: {},
      sources: opts?.sources || [],
      baseFacts: opts?.baseFacts || [],
      assumptions: opts?.assumptions || [],
    };
  }

  const { sources = [], baseFacts = [] } = opts;

  const colMap = resolveColumns(rows);

  // Only include a dimension if the column exists AND at least one non-blank value is present
  const dims = preferredOrder.filter((k) => {
    const col = colMap[k];
    return col && hasAnyNonBlank(rows, col);
  });

  const dimensions = dims.map((key) => ({ key, label: DIM_LABELS[key] }));

  // --- provenance maps (unchanged) ---
  const sourceById = Object.fromEntries(sources.map((s) => [s.source_id, s]));

  const factsBySourceId = baseFacts.reduce((acc, bf) => {
    const sid = bf.source_id;
    if (!sid) return acc;
    if (!acc[sid]) acc[sid] = [];
    acc[sid].push(bf);
    return acc;
  }, {});

  function getProvenanceForRows(levelRows) {
    const ids = new Set();
    for (const r of levelRows) (r.source_ids || []).forEach((id) => ids.add(id));
    const segSources = Array.from(ids).map((id) => sourceById[id]).filter(Boolean);
    const segFacts = segSources.flatMap((s) => factsBySourceId[s.source_id] || []);
    return { segSources, segFacts };
  }

  // --- totals for header ---
  const grandTotal = rows.reduce((s, r) => s + toNum(r[valueField]), 0);
  const reportedTotal = toNum(opts?.company?.annualRevenueOverride); // (millions)
  const modeledTotal = grandTotal;

  let totalRevenueDisplay = fmtMoneyAuto(modeledTotal);
  let totalRange = null;
  if (reportedTotal > 0 && modeledTotal > 0 && Math.abs(modeledTotal - reportedTotal) > 1e-6) {
    const lo = Math.min(reportedTotal, modeledTotal);
    const hi = Math.max(reportedTotal, modeledTotal);
    totalRange = {
      min: fmtMoneyAuto(lo),
      max: fmtMoneyAuto(hi),
      reported: fmtMoneyAuto(reportedTotal),
      modeled: fmtMoneyAuto(modeledTotal),
    };
    totalRevenueDisplay = `${totalRange.min} – ${totalRange.max}`;
  }

  // --- growth fields ---
  const growthFields =
    Array.isArray(opts.rowGrowthFields) && opts.rowGrowthFields.length
      ? opts.rowGrowthFields
      : DEFAULT_ROW_GROWTH_FIELDS;

  const avgGrowthComputed =
    (opts.context && typeof opts.context.avgGrowthPct === "number"
      ? opts.context.avgGrowthPct
      : undefined) ??
    weightedAvgGrowthFlexible(rows, valueField, growthFields) ??
    meanYoYFromPairs(opts.yearTotalsOverride) ??
    (typeof opts.defaultAvgGrowthPct === "number" ? opts.defaultAvgGrowthPct : undefined) ??
    0;

  // --- recursive builder ---
  function buildBreakdowns(levelRows, usedDims) {
    const remaining = dims.filter((k) => !usedDims.includes(k));
    if (!remaining.length) return undefined;

    const subsetTotal = levelRows.reduce((s, r) => s + toNum(r[valueField]), 0);
    const out = {};

    for (const dimKey of remaining) {
      const colName = colMap[dimKey];
      if (!colName) continue;

      const groups = groupBy(levelRows, (r) => {
        const raw = r[colName];
        const label = raw == null || String(raw).trim() === "" ? "Other" : String(raw);
        return label;
      });
      const entries = Array.from(groups.entries());

      // pass 1: totals / shares / explicit growth
      const items = entries.map(([label, rs]) => {
        const total = rs.reduce((s, r) => s + toNum(r[valueField]), 0);
        const share = subsetTotal > 0 ? total / subsetTotal : 0;
        const explicitGrowth = weightedAvgGrowthFlexible(rs, valueField, growthFields);
        return { label, rs, total, share, explicitGrowth };
      });

      // proportional fallback normalized to header avg
      const needFallback = items.filter((it) => !Number.isFinite(it.explicitGrowth));
      if (needFallback.length) {
        const N = items.length || 1;
        const meanShare = 1 / N;
        const raws = needFallback.map((it) => ({
          it,
          raw: avgGrowthComputed * (it.share / meanShare),
        }));
        const weightedRawSum = raws.reduce((acc, r) => acc + r.raw * r.it.share, 0);
        const norm = weightedRawSum > 0 ? avgGrowthComputed / weightedRawSum : 1;
        const lo = 0.25 * avgGrowthComputed;
        const hi = 1.5 * avgGrowthComputed;
        for (const r of raws) {
          const g = r.raw * norm;
          r.it.fallbackGrowth = +Math.min(Math.max(g, lo), hi).toFixed(1);
        }
      }

      // pass 2: build nodes
      const nodes = [];
      for (const it of items) {
        const nodeGrowth = Number.isFinite(it.explicitGrowth)
          ? it.explicitGrowth
          : Number.isFinite(it.fallbackGrowth)
          ? it.fallbackGrowth
          : avgGrowthComputed;

        const node = {
          label: it.label,
          value: fmtMoneyAuto(it.total),
          // ⬇ Share % now to 2 decimals
          sharePct: subsetTotal > 0 ? +((it.total / subsetTotal) * 100).toFixed(2) : 0,
          growthPct: Number.isFinite(nodeGrowth) ? nodeGrowth : 0,
          _total: it.total,
        };

        // provenance unchanged
        const { segSources, segFacts } = getProvenanceForRows(it.rs);
        node.sources = segSources;
        node.baseFacts = segFacts;

        // stop recursion for Application Market as requested
        node.breakdowns = buildBreakdowns(it.rs, [...usedDims, dimKey]);

        nodes.push(node);
      }

      nodes.sort((a, b) => b._total - a._total);
      out[dimKey] = nodes;
    }
    return out;
  }

  const allAtTop = buildBreakdowns(rows, []);
  const root = {};
  for (const startDim of dims) root[startDim] = allAtTop?.[startDim] || [];

  // final payload (also expose sources/baseFacts/assumptions at root for Excel)
  return {
    company: opts.company || { name: undefined },
    context: {
      fiscalYear: opts.context?.fiscalYear,
      totalRevenue: totalRevenueDisplay,
      totalRevenueReported: reportedTotal ? fmtMoneyAuto(reportedTotal) : undefined,
      totalRevenueModeled: fmtMoneyAuto(modeledTotal),
      totalRevenueRange: totalRange,
      avgGrowthPct: Number.isFinite(avgGrowthComputed) ? avgGrowthComputed : 0,
      ...opts.context,
    },
    dimensions,
    root,
    // keep these separate for Excel / details panes
    sources: opts?.sources || [],
    baseFacts: opts?.baseFacts || [],
    assumptions: opts?.assumptions || [],
  };
}
